import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, UserCog, Loader2, Power } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function StaffPage() {
  const qc = useQueryClient();
  const { data: staff = [], isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: api.listStaff,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });

  const createM = useMutation({
    mutationFn: () => api.createStaff(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success(`Staff created · temp password: ${form.password}`);
      setAddOpen(false);
      setForm({ email: "", name: "", password: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const toggleM = useMutation({
    mutationFn: ({ id, isActive }) => api.updateStaff(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const deleteM = useMutation({
    mutationFn: (id) => api.deleteStaff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Staff removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const canSubmit =
    form.email.trim() && form.name.trim() && form.password.length >= 8 && !createM.isPending;

  return (
    <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-6 flex flex-col gap-6" data-testid="staff-page">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
            <UserCog className="h-4 w-4 text-[#64748B]" /> Staff
          </h1>
          <p className="text-xs text-[#64748B]">Create staff accounts and assign clients to them from the Clients page.</p>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid="add-staff-btn"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#0F172A] text-white text-sm font-medium hover:bg-[#1E293B]"
        >
          <Plus className="h-4 w-4" /> Add Staff
        </button>
      </header>

      <div className="ab-card ab-shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-xs text-[#64748B]">Loading…</div>
        ) : staff.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#64748B]">
            No staff yet. Click <b>Add Staff</b> to create one.
          </div>
        ) : (
          <table className="ab-table w-full text-left">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th className="text-right">Assigned Clients</th>
                <th>Status</th><th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                  <td className="font-medium text-[#0F172A]">{s.name}</td>
                  <td className="text-[#334155]">{s.email}</td>
                  <td className="text-right tabular-nums">{s.assignedClients || 0}</td>
                  <td>
                    <span
                      className={cn(
                        "text-[11px] rounded px-1.5 py-0.5 font-medium",
                        s.isActive ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#991B1B]"
                      )}
                    >
                      {s.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => toggleM.mutate({ id: s.id, isActive: !s.isActive })}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC] mr-1"
                      title={s.isActive ? "Disable" : "Enable"}
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Delete ${s.name}?`)) deleteM.mutate(s.id);
                      }}
                      className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#FEF2F2] hover:text-[#DC2626] hover:border-[#FECACA]"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => !createM.isPending && setAddOpen(o)}>
        <DialogContent className="sm:max-w-md p-0 gap-0 border-[#E2E8F0]">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E2E8F0]">
            <DialogTitle className="text-base font-semibold text-[#0F172A]">
              New Staff Account
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">Name</span>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                data-testid="staff-name-input"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                data-testid="staff-email-input"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-[#334155]">Temporary Password (min 8 chars)</span>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm font-mono"
                data-testid="staff-password-input"
              />
              <span className="text-[10px] text-[#94A3B8]">
                Staff will be prompted to change this on first login.
              </span>
            </label>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] rounded-b-lg">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              disabled={createM.isPending}
              className="h-9 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm text-[#334155]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createM.mutate()}
              disabled={!canSubmit}
              data-testid="staff-submit"
              className="h-9 px-3.5 rounded-md bg-[#0F172A] text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
            >
              {createM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
